import * as R from 'ramda'
import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Loader, Modal, Message } from 'semantic-ui-react'
import { Elements, injectStripe, StripeProvider } from 'react-stripe-elements'

import { Card, UserInfo } from 'api/schema'
import { connectApi } from 'libs/connectApi'
import { getPublishableKey, isUsBilling } from 'libs/billing/stripe-account'
import toast from 'libs/toast'
import { PaymentForm } from 'components/PaymentForm'
import { Condition } from '../../components/Condition/index'
import useAxios, { mutate } from 'hooks/useAxios'
import useFeatureToggle from 'hooks/useFeatureToggle'
import useWorkspace from 'hooks/useWorkspace'
import { useTaxPreview } from 'hooks/useTaxPreview'
import { useTaxIdField } from 'hooks/useTaxIdField'
import { withRouter, Link } from 'react-router-dom'
import UnsavedChangesGuard from 'shared/components/UnsavedChangesGuard'
import { SubscriptionSummaryContainer } from 'components/SubscriptionSummary/container'
import { TaxIdField } from 'components/TaxIdField'

const EMPTY_TAX_ADDRESS = { name: '', address: null, complete: false }
const ADDRESS_FIELDS = ['line1', 'line2', 'city', 'state', 'postal_code', 'country']

function isCompleteSavedAddress(name, address) {
  const hasRequiredFields = Boolean(name && address.line1 && address.city && address.country)
  if (address.country === 'US') {
    return hasRequiredFields && Boolean(address.state && address.postal_code)
  }
  return hasRequiredFields
}

function toTaxAddress(savedBillingAddress) {
  const savedAddress = savedBillingAddress?.address
  if (!savedAddress?.country) {
    return null
  }
  const name = savedBillingAddress.name || ''
  const address = R.fromPairs(ADDRESS_FIELDS.map((field) => [field, savedAddress[field] || '']))
  return { name, address, complete: isCompleteSavedAddress(name, address) }
}

const NEW_SUBSCRIPTION_STATUSES = ['canceled', 'incomplete_expired', 'trialing']

function MissingSavedAddressMessage() {
  const workspaceToken = useWorkspace()
  return (
    <Message
      warning
      content={
        <React.Fragment>
          Sales tax is calculated from your billing address. Add it under Billing details on the{' '}
          <Link to={`/${workspaceToken}/settings/billing/`}>Billing page</Link>, then subscribe.
        </React.Fragment>
      }
    />
  )
}

function isPlanChange(billing) {
  const status = R.path(['subscription', 'status'], billing)
  return Boolean(status) && !NEW_SUBSCRIPTION_STATUSES.includes(status)
}

class PlanSubscriptionModal extends React.Component {
  static propTypes = {
    hasPaymentInfo: PropTypes.bool,
    isFeatureToggleLoading: PropTypes.bool,
    showSalesTax: PropTypes.bool,
    isPlanChange: PropTypes.bool,
    type: PropTypes.string,
    selectedPlan: PropTypes.object,
    stripe: PropTypes.object,
    subscribe: PropTypes.object,
    card: PropTypes.object,
    user: PropTypes.object,
    isModalOpened: PropTypes.object,
    onModalOpened: PropTypes.func,
    onModalClosed: PropTypes.func,
    location: PropTypes.object,
    history: PropTypes.object,
    variant: PropTypes.oneOf(['cardElement', 'paymentElement']),
    billingAddress: PropTypes.object,
    taxPreview: PropTypes.shape({ isLoading: PropTypes.bool, preview: PropTypes.object }),
    taxAddress: PropTypes.object,
    savedTaxAddress: PropTypes.object,
    isSavedAddressLoading: PropTypes.bool,
    onTaxAddressChange: PropTypes.func,
    taxId: PropTypes.shape({
      meta: PropTypes.object,
      value: PropTypes.string,
      error: PropTypes.string,
      isDirty: PropTypes.bool,
      onChange: PropTypes.func,
      save: PropTypes.func,
      reset: PropTypes.func,
    }),
  }

  state = {
    name: '',
    address: '',
    city: '',
    province: '',
    zip: '',
    isCardCompleted: false,
    // Tracks Stripe's `event.empty` signal — same discipline as updatePaymentModal.
    // Starts true (card fields are blank on mount). The correct dirty signal for
    // a half-typed invalid card that isCardCompleted would miss.
    isCardEmpty: true,
    loading: false,
    showWaringMessage: false,
    reachedLimitFeatureList: [],
    // Force-remount key for PaymentForm — mirrors updatePaymentModal.
    // The paymentElement variant's SetupIntent client_secret is single-use
    // and is spent by confirmSetup BEFORE the subscribe BE call runs. If
    // that call fails, we remount PaymentForm to fetch a fresh SetupIntent
    // (and reset the inner submit re-entry guard) so the user can retry.
    paymentElementRemountKey: 0,
  }

  initial_state = {
    name: '',
    address: '',
    city: '',
    province: '',
    zip: '',
    isCardCompleted: false,
    isCardEmpty: true,
    loading: false,
    showWaringMessage: false,
    reachedLimitFeatureList: [],
    paymentElementRemountKey: 0,
  }

  resetTaxAddress = () => {
    this.props.onTaxAddressChange(this.props.savedTaxAddress || EMPTY_TAX_ADDRESS)
  }

  resetState = () => {
    this.resetTaxAddress()
    this.props.taxId.reset()
    this.setState(this.initial_state)
  }

  hiddenSubmitButtonRef = React.createRef()

  onSubscribeUpdateParameterUrl = (status) => {
    const urlSearchParam = new URLSearchParams(this.props.location.search)
    if (status === true) {
      urlSearchParam.set('sign_up_success', 'true')
    } else {
      urlSearchParam.set('sign_up_success', 'false')
    }
    this.props.history.replace({ search: urlSearchParam.toString() })
  }

  onTextChange = (event) => {
    const { name, value } = event.target
    this.setState({ [name]: value })
  }

  onCardChange = (event) => {
    const update = {}
    if (typeof event.complete !== 'undefined') {
      update.isCardCompleted = event.complete
    }
    if (typeof event.empty !== 'undefined') {
      update.isCardEmpty = event.empty
    }
    this.setState(update)
  }

  isTaxPending = () => this.props.showSalesTax && this.props.taxPreview.isLoading

  collectsAddress = () => this.props.showSalesTax && !this.props.hasPaymentInfo

  isMissingSavedAddress = () =>
    this.props.showSalesTax &&
    this.props.hasPaymentInfo &&
    R.path(['taxPreview', 'preview', 'status'], this.props) === 'requires_location_inputs'

  isSubscribeBlocked = () =>
    this.props.isFeatureToggleLoading || this.isTaxPending() || this.isMissingSavedAddress()

  remountPaymentForm = () => {
    if (this.props.variant === 'paymentElement') {
      this.resetTaxAddress()
    }
    this.setState((prevState) => ({
      loading: false,
      ...(this.props.variant === 'paymentElement' && {
        isCardCompleted: false,
        isCardEmpty: true,
      }),
      paymentElementRemountKey:
        this.props.variant === 'paymentElement'
          ? prevState.paymentElementRemountKey + 1
          : prevState.paymentElementRemountKey,
    }))
  }

  isFormDirty = () => {
    const { name, address, city, province, zip, isCardEmpty } = this.state
    const hasOwnFieldChanged =
      name !== '' || address !== '' || city !== '' || province !== '' || zip !== ''
    return !isCardEmpty || hasOwnFieldChanged || this.props.taxId.isDirty
  }

  handleCloseModal = () => {
    const isDirty = this.isFormDirty()
    if (isDirty) {
      const confirmed = window.confirm(
        'Are you sure you want to leave? Changes you made may not be saved.'
      )
      if (!confirmed) {
        return
      }
    }
    this.resetState()
    this.props.onModalClosed()
  }

  handleOpenPrimaryModal = () => {
    this.resetState()
    this.props.onModalOpened('primary')
  }

  updateSubscription = (plan_id, stripe_token) => {
    this.props.subscribe
      .callApi({ data: { plan_id, stripe_token } })
      .then(async () => {
        await Promise.all([this.props.card.callApi(), this.props.user.callApi()])
        // A successful subscribe closes unconditionally — reset the FULL form
        // (all own fields + dirty flags via initial_state) and close directly,
        // bypassing the unsaved-changes guard. Routing through handleCloseModal
        // while resetting only name/isCardEmpty left address/city/province/zip
        // populated for the cardElement flow, so the guard's own-field check
        // re-fired the confirm dialog after a save (EC8 regression).
        this.resetState()
        this.props.onModalClosed()
        toast.success('Your subscription was successfully updated.')
        this.onSubscribeUpdateParameterUrl(true)
        mutate('billing/')
        mutate('billing/usage/')
        mutate('account/users/me/v2/')
      })
      .catch((error) => {
        this.remountPaymentForm()
        this.onSubscribeUpdateParameterUrl(false)

        const errorMessage = R.path(['data', 'detail'], error)
        const reachedLimitFeatureList = R.path(['data', 'features'], error) || []

        if (!R.isEmpty(reachedLimitFeatureList)) {
          this.setState({
            showWaringMessage: true,
            reachedLimitFeatureList: reachedLimitFeatureList,
          })
        } else if (errorMessage) {
          toast.error(errorMessage)
        } else {
          toast.error('Your subscription failed to update. Please try again.')
        }
      })
  }

  onSubmit = (event) => {
    event.preventDefault()
    this.setState({ loading: true })
    const planId = this.props.selectedPlan.id
    if (this.props.hasPaymentInfo) {
      this.updateSubscription(planId)
    } else {
      this.props.stripe
        .createToken({
          name: this.state.name,
          address_line1: this.state.address,
          address_city: this.state.city,
          address_state: this.state.province,
          address_zip: this.state.zip,
        })
        .then((response) => this.updateSubscription(planId, response.token.id))
    }
  }

  // paymentElement variant callback — PaymentFormPaymentElement runs its
  // own confirmSetup internally and hands back a `pm_...` id via onSaved.
  // Subscribe BE (`SubscriptionView.post`) already accepts either shape via
  // `add_card`'s prefix dispatch, so we just pass the pm_ through as
  // `stripe_token` and the field name mismatch stays server-side only.
  onPaymentMethodSaved = (paymentMethodId) => {
    this.updateSubscription(this.props.selectedPlan.id, paymentMethodId)
  }

  onPaymentMethodError = (message) => {
    this.setState({ loading: false })
    toast.error(message)
  }

  submitPaymentForm = () => {
    const input = this.hiddenSubmitButtonRef.current
    if (input && input.form) {
      input.form.requestSubmit(input)
      return
    }
    this.setState({ loading: false })
    toast.error('The payment form is still loading — please wait a moment and try again.')
  }

  getBillingAddressErrorMessage = (error) => {
    const data = R.pathOr({}, ['data'], error)
    if (data.detail) {
      return data.detail
    }
    const fieldMessages = R.flatten(
      R.values({ ...R.omit(['detail', 'address'], data), ...R.propOr({}, 'address', data) })
    )
      .filter((message) => typeof message === 'string')
      .join(' ')
    return fieldMessages || 'Your billing address failed to save. Please try again.'
  }

  saveTaxIdThenSubmit = () =>
    this.props.taxId.save().then((isTaxIdSaved) => {
      if (!isTaxIdSaved) {
        this.setState({ loading: false })
        return
      }
      this.submitPaymentForm()
    })

  onSaveClick = () => {
    // Immediately show loading — confirmSetup takes ~1-2s and the user
    // needs feedback. For the cardElement variant, onSubmit also sets it
    // (redundant but harmless).
    this.setState({ loading: true })
    if (!this.collectsAddress()) {
      this.saveTaxIdThenSubmit()
      return
    }
    const { name, address } = this.props.taxAddress
    const sentAddress = { name, address }
    this.props.billingAddress
      .callApi({ data: sentAddress })
      .then(() => {
        mutate('billing/customers/billing-address/')
        const { name: currentName, address: currentAddress } = this.props.taxAddress
        if (!R.equals(sentAddress, { name: currentName, address: currentAddress })) {
          this.setState({ loading: false })
          toast.error('Your address changed before saving finished. Select Subscribe again.')
          return
        }
        return this.saveTaxIdThenSubmit()
      })
      .catch((error) => {
        this.setState({ loading: false })
        toast.error(this.getBillingAddressErrorMessage(error))
      })
  }

  renderTaxIdField = () => {
    const { taxId } = this.props
    if (!taxId.meta) {
      return null
    }
    return (
      <TaxIdField
        meta={taxId.meta}
        value={taxId.value}
        error={taxId.error}
        onChange={taxId.onChange}
      />
    )
  }

  render() {
    const isDirty = this.isFormDirty()
    return (
      <React.Fragment>
        <UnsavedChangesGuard when={isDirty} guardRefresh={false} />
        <Modal open={this.props.isModalOpened.secondary} onClose={this.handleCloseModal}>
          <Modal.Header>
            <h1 className="ui center aligned header">{`${this.props.selectedPlan.nickname} Plan`}</h1>
          </Modal.Header>
          <Modal.Content>
            <h4 className="ui dividing header">Subscription summary</h4>
            <SubscriptionSummaryContainer
              selectedPlan={this.props.selectedPlan}
              type={this.props.type}
              coupon={this.props.coupon}
              hasTax={this.props.showSalesTax}
              collectsAddress={this.collectsAddress()}
              isPlanChange={this.props.isPlanChange}
              taxPreview={this.props.taxPreview}
            />
            {this.state.showWaringMessage && (
              <Message
                warning
                header="Action required: unable to downgrade"
                content={
                  <React.Fragment>
                    <p>
                      Your organization currently exceeds the feature usage limits for the desired
                      plan. To downgrade, please reduce your usage as outlined below:
                    </p>
                    <ul>
                      {this.state.reachedLimitFeatureList.map((data) => {
                        return (
                          <li key={data.name}>
                            <strong>{data.name}:</strong> Current usage - {data.usage}
                            {data.limit != '0'
                              ? ` (Limit for downgrading - ${data.limit})`
                              : ` (No dashboard allowed on lower tier)`}
                          </li>
                        )
                      })}
                    </ul>
                    <p>
                      Adjust your usage on the
                      <Link to={'/settings/usage'}> usage stats page</Link>, or contact{' '}
                      <a href="mailto:support@simplesat.io">Simplesat support</a> for assistance.
                    </p>
                  </React.Fragment>
                }
              />
            )}
            {this.isMissingSavedAddress() && <MissingSavedAddressMessage />}

            {this.props.hasPaymentInfo ? null : (
              <React.Fragment>
                <h4 className="ui dividing header">Payment Information</h4>
                {this.props.isFeatureToggleLoading || this.props.isSavedAddressLoading ? (
                  <Loader active inline="centered" data-testid="payment-form-loader" />
                ) : (
                  <PaymentForm
                    key={this.state.paymentElementRemountKey}
                    variant={this.props.variant}
                    name={this.state.name}
                    address={this.state.address}
                    city={this.state.city}
                    province={this.state.province}
                    zip={this.state.zip}
                    onCardChange={this.onCardChange}
                    onSubmit={this.onSubmit}
                    onTextChange={this.onTextChange}
                    onSaved={this.onPaymentMethodSaved}
                    onError={this.onPaymentMethodError}
                    forwardedRef={this.hiddenSubmitButtonRef}
                    onAddressChange={
                      this.collectsAddress() ? this.props.onTaxAddressChange : undefined
                    }
                    defaultAddress={this.props.savedTaxAddress}
                    taxIdField={this.renderTaxIdField()}
                  />
                )}
              </React.Fragment>
            )}
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={this.handleOpenPrimaryModal}>Back</Button>
            <Condition condition={this.props.hasPaymentInfo}>
              <Condition.True>
                <Button
                  primary
                  type="submit"
                  onClick={this.onSubmit}
                  loading={this.state.loading}
                  disabled={
                    (!this.props.hasPaymentInfo && !this.state.isCardCompleted) ||
                    this.state.loading ||
                    this.isSubscribeBlocked()
                  }
                >
                  Subscribe
                </Button>
              </Condition.True>
              <Condition.False>
                <Button
                  primary
                  type="submit"
                  loading={this.state.loading}
                  disabled={
                    (!this.props.hasPaymentInfo && !this.state.isCardCompleted) ||
                    this.state.loading ||
                    this.isSubscribeBlocked()
                  }
                  onClick={this.onSaveClick}
                >
                  Subscribe
                </Button>
              </Condition.False>
            </Condition>
          </Modal.Actions>
        </Modal>
      </React.Fragment>
    )
  }
}

const EnhancedPlanSubscriptionModal = R.compose(
  withRouter,
  injectStripe,
  connectApi({
    name: 'user',
    url: 'account/users/me/v2/',
    schema: UserInfo,
    skip: true,
  }),
  connectApi({
    name: 'subscribe',
    url: 'billing/subscription/',
    method: 'post',
    skip: true,
  }),
  connectApi({
    name: 'billingAddress',
    url: 'billing/customers/billing-address/',
    method: 'patch',
    skip: true,
  }),
  connectApi({
    name: 'card',
    url: 'billing/cards/',
    schema: Card,
    skip: true,
  })
)(PlanSubscriptionModal)

export const UpdatePlanSubscriptionModal = ({
  hasPaymentInfo,
  type,
  selectedPlan,
  isModalOpened,
  onModalOpened,
  onModalClosed,
  coupon,
  billing,
}) => {
  const {
    isLoading: isFeatureToggleLoading,
    enableBillingTaxId,
    enableBillingUsTax,
  } = useFeatureToggle()
  const publishableKey = getPublishableKey(billing)
  const usBilled = isUsBilling(billing)
  const showSalesTax = usBilled && !!enableBillingUsTax
  const collectsAddress = showSalesTax && !hasPaymentInfo
  const variant = enableBillingTaxId || usBilled ? 'paymentElement' : 'cardElement'
  const showsPaymentElement = variant === 'paymentElement' && !hasPaymentInfo
  const showsTaxId = !!enableBillingTaxId && !hasPaymentInfo
  const { data: savedBillingAddress, error: savedBillingAddressError } = useAxios({
    url: 'billing/customers/billing-address/',
    skip: !showsPaymentElement,
  })
  const isSavedAddressLoading =
    showsPaymentElement && savedBillingAddress == null && !savedBillingAddressError
  const savedTaxAddress = useMemo(() => toTaxAddress(savedBillingAddress), [savedBillingAddress])
  const [taxAddress, setTaxAddress] = useState(EMPTY_TAX_ADDRESS)
  const [seededTaxAddress, setSeededTaxAddress] = useState(null)
  if (!R.equals(savedTaxAddress, seededTaxAddress)) {
    setSeededTaxAddress(savedTaxAddress)
    if (R.equals(taxAddress, seededTaxAddress || EMPTY_TAX_ADDRESS)) {
      setTaxAddress(savedTaxAddress || EMPTY_TAX_ADDRESS)
    }
  }
  const taxPreview = useTaxPreview({
    enabled: showSalesTax && Boolean(selectedPlan.id),
    planId: selectedPlan.id,
    address: collectsAddress ? taxAddress.address : null,
    addressComplete: taxAddress.complete,
    requiresAddress: collectsAddress,
  })
  const taxId = useTaxIdField({
    enabled: showsTaxId,
    country: collectsAddress ? taxAddress.address?.country : savedBillingAddress?.address?.country,
  })
  return (
    <StripeProvider key={publishableKey} apiKey={publishableKey}>
      <Elements>
        <EnhancedPlanSubscriptionModal
          hasPaymentInfo={hasPaymentInfo}
          isFeatureToggleLoading={Boolean(isFeatureToggleLoading)}
          type={type}
          selectedPlan={selectedPlan}
          isModalOpened={isModalOpened}
          onModalOpened={onModalOpened}
          onModalClosed={onModalClosed}
          coupon={coupon}
          variant={variant}
          showSalesTax={showSalesTax}
          isPlanChange={isPlanChange(billing)}
          taxPreview={taxPreview}
          taxAddress={taxAddress}
          onTaxAddressChange={setTaxAddress}
          savedTaxAddress={savedTaxAddress}
          isSavedAddressLoading={isSavedAddressLoading}
          taxId={taxId}
        />
      </Elements>
    </StripeProvider>
  )
}

UpdatePlanSubscriptionModal.propTypes = {
  hasPaymentInfo: PropTypes.bool,
  type: PropTypes.string,
  selectedPlan: PropTypes.object,
  isModalOpened: PropTypes.object,
  onModalOpened: PropTypes.func,
  onModalClosed: PropTypes.func,
  billing: PropTypes.object,
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/Billing/updatePlanSubscriptionModal.js